import React, { useEffect, useState } from 'react';
import { UserCog, Loader2, Star, Bike } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

/**
 * ReassignDriverButton — small inline control shown on each vendor order row.
 * Opens a dialog with the zone-filtered driver list + a "Broadcast to Super
 * Drivers" option. Blocked on terminal-status orders by the backend.
 */
const ReassignDriverButton = ({ orderId, currentDriverId, currentDriverName, impersonating, vendorId, onReassigned }) => {
  const [open, setOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const params = impersonating && vendorId ? { vendor_id: vendorId } : undefined;

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/vendor-admin/preferred-driver', { params })
      .then((r) => setDrivers(r.data?.drivers || []))
      .catch(() => toast.error('Could not load drivers'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [open]);

  const reassignTo = async (driver_id, prefer_super = false) => {
    setBusy(true);
    try {
      const r = await api.post(`/vendor-admin/orders/${orderId}/reassign`,
        { driver_id, prefer_super }, { params });
      toast.success(prefer_super
        ? 'Broadcast to Super Drivers — the next one picks it up.'
        : `Sent to ${r.data?.driver_name || 'driver'}. They'll see it in their Available Deliveries.`);
      setOpen(false);
      onReassigned?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reassign failed'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="text-slate-700" data-testid={`vendor-reassign-btn-${orderId}`}>
        <UserCog className="h-3.5 w-3.5 mr-1" />Reassign
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-emerald-600" />Reassign order {String(orderId).slice(-6)}
            </DialogTitle>
            <DialogDescription>
              {currentDriverId ? <>Currently assigned to <span className="font-medium">{currentDriverName || currentDriverId}</span>.</> : 'No driver assigned yet.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Button
              type="button"
              onClick={() => reassignTo(null, true)}
              disabled={busy}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid={`vendor-reassign-super-${orderId}`}
            >
              <Star className="h-4 w-4 mr-1 fill-white" />Broadcast to Super Drivers
            </Button>
            <div className="text-[11px] text-slate-500 text-center">— or pick a specific driver in your zone —</div>
            {loading ? (
              <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
            ) : drivers.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4">No drivers available in your zone right now.</div>
            ) : (
              <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid={`vendor-reassign-list-${orderId}`}>
                {drivers.map((d) => (
                  <button
                    key={d.driver_id}
                    type="button"
                    onClick={() => reassignTo(d.driver_id, false)}
                    disabled={busy || d.driver_id === currentDriverId}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`vendor-reassign-pick-${orderId}-${d.driver_id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Bike className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-sm text-slate-800 truncate">{d.name}</span>
                      {d.is_super_driver && <Badge className="bg-indigo-100 text-indigo-800 border-0 hover:bg-indigo-100 text-[10px]">★ Super</Badge>}
                      <span className="text-[11px] text-slate-400">· {d.vehicle_type || 'Vehicle'}</span>
                    </div>
                    <span className={`text-[11px] font-medium ${d.is_online ? 'text-emerald-700' : 'text-slate-400'}`}>{d.is_online ? 'Online' : 'Offline'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReassignDriverButton;
